import {
  consentStatusResponseSchema,
  listConsentEventsResponseSchema,
  type ConsentJournalScope,
  type ListConsentEventsResponse,
  marketingSubscriptionResponseSchema,
  type ConsentEvent as ConsentEventContract,
  type ConsentStatusResponse,
  type GrantableConsentType,
  type MarketingSubscriptionResponse,
  type WithdrawableConsentType,
} from '@aica/api-types/schemas';
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';

/**
 * Типы согласий приходят из `@aica/api-types` — здесь только реэкспорт.
 *
 * Раньше тот же набор был выписан руками вторым литеральным union'ом, и
 * разъехаться две копии могли молча: сужение `participantConsentTypeSchema`
 * до одного `recording` не уронило бы ни один экран, продолжающий считать,
 * что типов четыре. Zod-схема — SSOT контракта, локальная копия — способ
 * от неё отстать.
 */
export type {
  ConsentEventType,
  ConsentStatusResponse,
  ConsentType,
  ParticipantConsentType,
} from '@aica/api-types/schemas';

/**
 * Событие журнала в том виде, в каком его отдаёт `GET /api/v1/consent`.
 *
 * `metadata` в контракте — `unknown`: столбец хранит произвольный JSON
 * вызывающей стороны, и интерфейс его не читает.
 */
export type ConsentEvent = ConsentEventContract;

/**
 * Что можно отозвать — из общего контракта, а не выведено здесь.
 *
 * Выводилось как `ParticipantConsentType | 'marketing_email'`, и это было уже
 * не то же самое: `participantConsentTypeSchema` сузился до одного `recording`,
 * а маршрут отзыва намеренно продолжает принимать исторические
 * `analysis`/`storage`/`crm_delivery` — иначе право на отзыв осталось бы без
 * способа им воспользоваться (Art. 7(3)). Локальная формула тихо отрезала их
 * от типизированных вызывающих: блок исторических согласий мог позвать отзыв
 * только через приведение типа.
 */
export type { WithdrawableConsentType } from '@aica/api-types/schemas';

export interface ComplianceConfirmResponse {
  status: string;
  confirmedBy: string;
  confirmedAt: string;
}

export class ConsentApi extends BaseApiClient {
  /**
   * Через схему, как `getStatus`: ответ, потерявший поле или принёсший курсор
   * не строкой, иначе доехал бы до листания как есть и вернулся бы серверу
   * чужим курсором (код-ревью #1455).
   */
  async listEvents(params?: {
    type?: string;
    /** Which half of the journal: participant consents or the account's agreement (#1100). */
    scope?: ConsentJournalScope;
    limit?: number;
    /** Where the previous page ended (#1397); omit for the first page. */
    cursor?: string;
    /** Only against a server from before the cursor (#1397) — see `useConsentJournal`. */
    offset?: number;
  }): Promise<ListConsentEventsResponse> {
    const query = this.buildQuery(params);
    return this.validatedRequest(
      `${API_ENDPOINTS.CONSENT}${query}`,
      listConsentEventsResponseSchema
    );
  }

  /**
   * Через схему, а не голым `request<T>`: приведение типом не проверяет
   * ничего, и ответ, потерявший поле, доезжал бы до экрана как `undefined` —
   * то есть карточка молча читалась бы «не зафиксировано». Маршрут отдаёт
   * этот ответ через ту же `consentStatusResponseSchema`, поэтому разъехаться
   * концы могут только вместе.
   */
  async getStatus(): Promise<ConsentStatusResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CONSENT_STATUS,
      consentStatusResponseSchema
    );
  }

  /**
   * Отдельный запрос, а не поле в `getStatus`: тот отвечает про организацию
   * (последнее событие орга, кто бы его ни оставил), а подписка на рассылку —
   * про конкретного человека. Один ответ на два вопроса означал бы, что
   * подписка коллеги читается как своя.
   */
  async getMarketing(): Promise<MarketingSubscriptionResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CONSENT_MARKETING,
      marketingSubscriptionResponseSchema
    );
  }

  /**
   * Сервер отвечает `{ id, status }`, а не `{ event }`: заявленная здесь
   * прежде форма не встречалась ни на одной ветке, и читатель `res.event.id`
   * получал `undefined` без единой ошибки типов.
   *
   * Тип — `GrantableConsentType`, а не весь `ConsentType`. Приём сужен до трёх
   * значений, которые продукт умеет записать по просьбе клиента; с широким
   * типом вызывающий мог передать `recording` или исторический `analysis`,
   * успешно собраться и получить 400 в рантайме. Схема — SSOT контракта,
   * поэтому отказ обязан наступать в компиляторе.
   */
  async grant(
    consentType: GrantableConsentType
  ): Promise<{ id: string | null; status: string }> {
    return this.request<{ id: string | null; status: string }>(
      API_ENDPOINTS.CONSENT_GRANT,
      {
        method: 'POST',
        body: JSON.stringify({ consentType }),
      }
    );
  }

  /**
   * `WithdrawableConsentType`, а не `ParticipantConsentType`: сервер принимает
   * здесь ещё и `marketing_email` (#308), и более узкий тип на клиенте
   * означал, что отписаться из интерфейса нельзя было в принципе — при том что
   * отзыв обязан быть не сложнее выдачи.
   */
  async withdraw(consentType: WithdrawableConsentType): Promise<{
    status: string;
    consentType: string;
    /**
     * Остановил ли этот отзыв приём разговоров.
     *
     * `false` у организации, заявившей `consentRequired: false`: гейт приёма
     * читает подтверждение только внутри этого условия, поэтому снятие
     * подтверждения её обработку не трогает. Без этого поля тост говорил бы
     * «согласие отозвано» одинаково там, где приём остановлен, и там, где он
     * продолжается.
     *
     * Отсутствует у всех типов, кроме `recording`: отзыв исторического
     * согласия или подписки на рассылку приёма не касается вовсе, и `false`
     * там читался бы как ответ «не остановил» — с предупреждением про
     * правовое основание, к которому это действие отношения не имеет.
     */
    processingStopped?: boolean;
  }> {
    return this.request<{
      status: string;
      consentType: string;
      processingStopped?: boolean;
    }>(API_ENDPOINTS.CONSENT_WITHDRAW, {
      method: 'POST',
      body: JSON.stringify({ consentType }),
    });
  }

  async confirmCompliance(): Promise<ComplianceConfirmResponse> {
    return this.request<ComplianceConfirmResponse>(
      API_ENDPOINTS.CONSENT_CONFIRM_COMPLIANCE,
      { method: 'POST' }
    );
  }
}
